<script setup>
  import { ref, computed, onMounted, onUnmounted, watchEffect, watch } from 'vue';
  import useContainerConfigurationGlobals from '@/composables/useContainerConfigurationGlobals';
  import useLegacyBridge from "@/composables/useLegacyBridge";
  
  const { $WIND_GRAPH } = useContainerConfigurationGlobals();

  const content = defineModel('content');

  const props = defineProps({
    isNCWind: {
      type: Boolean,
      required: true
    },
    ncWindDataEnabled: {
      type: Boolean,
      required: false
    },
    selectedModelIds: {
      type: [Array, Object],
      required: false
    },
    useExternalDataBlock: {
      type: Boolean,
      default: false
    },
    useExternalControls: {
      type: Boolean,
      default: false
    },
    useClassicModelSelector: {
      type: Boolean,
      default: false
    },
    multiStationGraphData: {
      type: Object,
      required: false
    },
    spotDataObject: {
      type: Object,
      required: true
    },
    dataField: {
      type: String,
      default: null
    },
    updateKey: {
      type: Number,
      default: () => Date.now()
    } 
  })

  const { currentConditions } = useLegacyBridge();

  const graphContainer = ref(null);
  const ncWindDataEnabled = ref(props.ncWindDataEnabled || false);

  const multiStationSettings = computed(() => {
    return {
        width: "100%",
        spotList: `${props.spotDataObject.spotId}`,
        spotId: props.spotDataObject.spotId,
        favSpotId: props.spotDataObject.spotFavoriteId,
        lat: props.spotDataObject.lat,
        lon: props.spotDataObject.lon,        
        spotName: props.spotDataObject.spotName,
        debug: "false",
        upgradeAvailable: props.spotDataObject.upgradeAvailable,
        dataField: props.dataField,
        showDataFieldsMenu: $WIND_GRAPH.show_data_fields_menu,
        showNearbySpots: $WIND_GRAPH.show_nearby_spots,
        hideData: false,
        showVirtual: $WIND_GRAPH.show_virtual,
        stationCapabilities: props.spotDataObject.stationCapabilities,
        showCurrentConditions: false,
        useExternalControls: props.useExternalControls,
        useExternalDataBlock: props.useExternalDataBlock,
        useClassicModelSelector: props.useClassicModelSelector,
    };
  });

  const singleStationSettings = computed(() => {
    const station = props.spotDataObject.station;

    return {
        width: "100%",
        spotId: props.spotDataObject.spotId,
        favSpotId: props.spotDataObject.spotFavoriteId,
        stationId: station.station_id,
        lat: props.spotDataObject.lat,
        lon: props.spotDataObject.lon,
        spotName: props.spotDataObject.spotName,
        debug: "false",
        spotStatus: station.status_id,
        provider: station.provider,
        spotTypeId: station.Type,
        upgradeAvailable: props.spotDataObject.upgradeAvailable,
        dataField: props.dataField,
        showDataFieldsMenu: $WIND_GRAPH.show_data_fields_menu,
        showNearbySpots: $WIND_GRAPH.show_nearby_spots,
        showVirtual: $WIND_GRAPH.show_virtual,
        showCurrentConditions: false,
        stationCapabilities: props.spotDataObject.stationCapabilities,
        hideData: true,
        isNCWind: props.isNCWind,
        ncWindDataEnabled: ncWindDataEnabled.value,
        useExternalControls: props.useExternalControls,
        useExternalDataBlock: props.useExternalDataBlock,
        useClassicModelSelector: props.useClassicModelSelector,
    };
  });

  function loadGraph() {
    const settings = props.spotDataObject.isMultiStationSpot ? multiStationSettings.value : singleStationSettings.value;     
    
    if (props.selectedModelIds) {
      settings.selectedModelIds = props.selectedModelIds
    }
    content.value = currentConditions(graphContainer.value, settings);

    watchEffect(() => {
      if (!props.multiStationGraphData) return;

      window.$EventBus.dispatchEvent('multi-station-list-change', props.multiStationGraphData);
    });
  }

  onMounted(() => {
    loadGraph();

    watchEffect(() => ncWindDataEnabled.value = props.ncWindDataEnabled || false);
  })

  onUnmounted(() => {
    content.value?.destroy?.();
  });

  watch(() => props.updateKey, () => {
    content.value.refresh();
  })

</script>

<template>
  <div
    :id="props.spotDataObject.spotId"
    class="jwx-data-container__main-content"
    style="width: 100%;"
    ref="graphContainer"
  ></div>
</template>